import { useState } from 'react'

import { createLazy, getFacingCount, getValueOfCssVariable } from '@/lib/utils'
import { AGE_BAND_VARIABLE, AGE_BANDS } from '@components/storeMap/dataAge'
import { useStoreMapContext } from '@components/storeMap/provider/StoreMapContext'
import { ageBandOf } from '@state/shelf/transformations/dayState'

import NoDataShelf from './NoDataShelf'
import Shelf from './Shelf'

import type { AgeBand, TransformedShelf } from '@state/shelf/types'

interface ContextAwareShelfProps {
  shelf: TransformedShelf
}

// initialize the colors lazily because it is possible that this gets evaluated before CSS has been loaded
// resulting in missing colors
const getColorsForShelf = createLazy(() => {
  return {
    noFacingsColor: getValueOfCssVariable('--color-shelf-no-facings'),
    selectedColor: getValueOfCssVariable('--color-shelf-selected'),
    selectedSecondaryColor: getValueOfCssVariable('--color-shelf-selected-secondary'),

    oosColor: getValueOfCssVariable('--color-out-of-shelf'),
    oosSecondaryColor: getValueOfCssVariable('--color-out-of-shelf-secondary'),

    neutralColor: getValueOfCssVariable('--color-shelf-neutral'),
    noDataColor: getValueOfCssVariable('--color-shelf-border'),

    ageColors: Object.fromEntries(AGE_BANDS.map(band => [band, getValueOfCssVariable(AGE_BAND_VARIABLE[band])])) as Record<AgeBand, string>,
  }
})

function ContextAwareShelf({ shelf }: ContextAwareShelfProps) {
  const [isHovered, setIsHovered] = useState(false)
  const { selectedShelfId, setSelectedShelfId, highlightedShelves, isLayerActive } = useStoreMapContext()

  const { selectedColor, selectedSecondaryColor, oosColor, oosSecondaryColor, noFacingsColor, neutralColor, noDataColor, ageColors } = getColorsForShelf()

  const isOOSShelf = (shelf.statistics?.noStockCount ?? 0) > 0
  const mySelectedColor = isOOSShelf ? oosColor : selectedColor
  const mySelectedSecondaryColor = isOOSShelf ? oosSecondaryColor : selectedSecondaryColor

  const hasFacings = getFacingCount(shelf) > 0

  const isSelected = shelf.id === selectedShelfId
  const isHighlighted = highlightedShelves.includes(shelf.id)

  // Color logic:
  //   * by default no fill overwrite is set
  //   * if the shelf has no facings, it gets the no-facings color
  //   * if a shelf is selected, it gets the selected color
  //   * if a shelf is highlighted, it gets the highlighted color, but only if no other shelf is selected
  //   * if a shelf is highlighted and another shelf is selected, it gets the secondary highlighted color
  //   * if a shelf is hovered, it gets the selected color (overrides both selected and highlighted states)

  let fill = undefined
  if (!hasFacings) {
    fill = noFacingsColor
  }

  // not judged: with a stock layer on it must not read as "no gaps"
  if (shelf.dayState === 'notCurrent' && (isLayerActive('HIGHLIGHT_OUT_OF_SHELF') || isLayerActive('HEATMAP_UNKNOWNS'))) {
    fill = neutralColor
  }

  if (isLayerActive('DATA_AGE') && shelf.daysBeforeDay !== undefined) {
    fill = ageColors[ageBandOf(shelf.daysBeforeDay)]
  }

  if (isHovered) {
    fill = mySelectedColor
  } else if (isHighlighted) {
    fill = mySelectedColor
    if (selectedShelfId && selectedShelfId !== shelf.id) {
      fill = mySelectedSecondaryColor
    }
  } else if (isSelected) {
    fill = mySelectedColor
  }

  if (shelf.dayState === 'noData') return <NoDataShelf shelf={shelf} color={noDataColor} />

  return (
    <Shelf
      fill={fill}
      shelf={shelf}
      onMouseOver={() => setIsHovered(true)}
      onMouseOut={() => setIsHovered(false)}
      onClick={() => setSelectedShelfId(shelf.id)}
    />
  )
}

export default ContextAwareShelf